import React from 'react';

export const SkeletonCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
    <div className="animate-pulse space-y-4">
      <div className="h-4 bg-secondary rounded w-1/4"></div>
      <div className="h-8 bg-secondary rounded w-1/2"></div>
      <div className="h-3 bg-secondary rounded w-3/4"></div>
    </div>
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4
}) => (
  <div className="bg-card rounded-lg shadow overflow-hidden">
    <div className="p-6">
      <div className="animate-pulse space-y-4">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array.from({ length: columns }).map((_, i) => (
            <div key={`header-${i}`} className="h-4 bg-muted rounded"></div>
          ))}
        </div>

        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className="grid gap-4"
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div
                key={`cell-${rowIndex}-${colIndex}`}
                className="h-4 bg-secondary rounded"
              ></div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const SkeletonList: React.FC<{ items?: number }> = ({ items = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: items }).map((_, i) => (
      <div key={`item-${i}`} className="bg-card rounded-lg shadow p-4">
        <div className="animate-pulse space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-secondary rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-secondary rounded w-1/3"></div>
              <div className="h-3 bg-secondary rounded w-1/2"></div>
            </div>
          </div>
          <div className="h-3 bg-secondary rounded w-full"></div>
          <div className="h-3 bg-secondary rounded w-5/6"></div>
        </div>
      </div>
    ))}
  </div>
);

export const SkeletonDashboard: React.FC = () => (
  <div className="min-h-screen bg-background">
    <div className="px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-8">
      {/* Header Skeleton */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 sm:mb-8">
        <div className="animate-pulse space-y-2">
          <div className="h-8 bg-secondary rounded w-48"></div>
          <div className="h-4 bg-secondary rounded w-96"></div>
        </div>
        <div className="animate-pulse h-10 bg-secondary rounded w-full md:w-48"></div>
      </div>

      {/* Overview Cards Skeleton */}
      <div className="mb-6 sm:mb-8">
        <div className="animate-pulse h-6 bg-secondary rounded w-24 mb-4"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`overview-${i}`} className="bg-card rounded-xl shadow-md p-4 sm:p-6 border border-border">
              <div className="animate-pulse">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-secondary rounded-xl"></div>
                  <div className="ml-3 sm:ml-4 flex-1 space-y-2">
                    <div className="h-3 bg-secondary rounded w-2/3"></div>
                    <div className="h-6 bg-secondary rounded w-1/2"></div>
                    <div className="h-3 bg-secondary rounded w-1/3"></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Skeleton */}
      <div className="mb-6 sm:mb-8">
        <div className="animate-pulse h-6 bg-secondary rounded w-32 mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`activity-${i}`} className="bg-card rounded-xl shadow-md p-4 sm:p-6 border border-border">
              <div className="animate-pulse space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-secondary rounded-xl"></div>
                    <div className="ml-3 sm:ml-4 space-y-2">
                      <div className="h-3 bg-secondary rounded w-24"></div>
                      <div className="h-6 bg-secondary rounded w-16"></div>
                    </div>
                  </div>
                  <div className="h-4 bg-secondary rounded w-20"></div>
                </div>
                <div className="space-y-3">
                  {Array.from({ length: 2 }).map((_, j) => (
                    <div key={`activity-item-${i}-${j}`} className="space-y-2">
                      <div className="flex justify-between">
                        <div className="h-4 bg-secondary rounded w-32"></div>
                        <div className="h-4 bg-secondary rounded w-20"></div>
                      </div>
                      <div className="h-3 bg-secondary rounded w-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Insights Skeleton */}
      <div className="mb-6 sm:mb-8">
        <div className="animate-pulse h-6 bg-secondary rounded w-40 mb-4"></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={`insight-${i}`} className="bg-card rounded-xl shadow-md p-4 sm:p-6 border border-border">
              <div className="animate-pulse space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-secondary rounded-lg"></div>
                  <div className="h-4 bg-secondary rounded w-32"></div>
                </div>
                <div className="h-8 bg-secondary rounded w-24"></div>
                <div className="h-4 bg-secondary rounded w-40"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const SkeletonForm: React.FC<{ fields?: number }> = ({ fields = 5 }) => (
  <div className="bg-card rounded-lg shadow p-6">
    <div className="animate-pulse space-y-6">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={`field-${i}`} className="space-y-2">
          <div className="h-4 bg-secondary rounded w-1/4"></div>
          <div className="h-10 bg-secondary rounded w-full"></div>
        </div>
      ))}

      <div className="flex justify-end gap-4 pt-4">
        <div className="h-10 bg-secondary rounded w-24"></div>
        <div className="h-10 bg-secondary rounded w-24"></div>
      </div>
    </div>
  </div>
);

export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className = ''
}) => (
  <div className={`animate-pulse space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={`line-${i}`}
        className="h-4 bg-secondary rounded"
        style={{
          width: i === lines - 1 ? '75%' : '100%'
        }}
      ></div>
    ))}
  </div>
);
